/**
 * Crypto Store (Zustand Vanilla & React)
 *
 * Ephemeral in-memory store for session-bound encryption seed and derived CryptoKeys.
 * Enforces zero-persistence and memory-bounded containment.
 */

import { createStore } from 'zustand/vanilla';
import { useStore } from 'zustand';
import logger from '../services/loggerService';
import { AUTH_SESSION_KEYS } from '../constants/securityConstants';

export interface CryptoState {
  seed: string | null;
  keyCache: Map<string, CryptoKey>;
}

export interface CryptoActions {
  setSeed: (seed: string | null) => void;
  clearSeed: () => void;
  cacheKey: (salt: string, key: CryptoKey) => void;
  getCachedKey: (salt: string) => CryptoKey | undefined;
}

/** Vanilla Zustand store for usage outside React component tree (e.g. Services, Utils) */
export const cryptoStore = createStore<CryptoState & CryptoActions>((set, get) => ({
  seed: null,
  keyCache: new Map<string, CryptoKey>(),

  setSeed: (seed) => set({ seed }),
  clearSeed: () => {
    // Clear keyCache explicitly
    const { keyCache } = get();
    keyCache.clear();
    set({ seed: null, keyCache: new Map<string, CryptoKey>() });
  },
  cacheKey: (salt, key) => {
    const { keyCache } = get();
    const MAX_CACHE_SIZE = 10;
    if (keyCache.size >= MAX_CACHE_SIZE) {
      const oldestKey = keyCache.keys().next().value;
      if (oldestKey !== undefined) {
        keyCache.delete(oldestKey);
      }
    }
    keyCache.set(salt, key);
    set({ keyCache: new Map(keyCache) });
  },
  getCachedKey: (salt) => get().keyCache.get(salt),
}));

/** React hook for usage inside React components */
export const useCryptoStore = <T>(selector: (state: CryptoState & CryptoActions) => T): T => 
  useStore(cryptoStore, selector);

// Bootstrap: Recover transient seed from sessionStorage and remove it
if (typeof window !== 'undefined') {
  try {
    const recovered = window.sessionStorage.getItem(AUTH_SESSION_KEYS.ENCRYPTION_SEED);
    if (recovered) {
      cryptoStore.setState({ seed: recovered });
      window.sessionStorage.removeItem(AUTH_SESSION_KEYS.ENCRYPTION_SEED);
    }
  } catch (error) {
    logger.warn('Failed to recover transient encryption seed from sessionStorage', { error });
  }

  // Intercept tab unload/pagehide to stash seed temporarily
  window.addEventListener('beforeunload', () => {
    const { seed } = cryptoStore.getState();
    if (seed) {
      try {
        window.sessionStorage.setItem(AUTH_SESSION_KEYS.ENCRYPTION_SEED, seed);
      } catch (error) {
        logger.error('Failed to store transient encryption seed before unload', { error });
      }
    }
  });

  window.addEventListener('pagehide', () => {
    const { seed } = cryptoStore.getState();
    if (seed) {
      try {
        window.sessionStorage.setItem(AUTH_SESSION_KEYS.ENCRYPTION_SEED, seed);
      } catch (error) {
        // Safe fallback
      }
    }
  });
}

export default useCryptoStore;
